/*

# `Point` At A Glance

Point can be used a Euclidean point or 2D vector. It:
- is immutable.
- has `x` and `y` properties.
- has a constructor that takes:
  - x and y arguments, or
  - an object with x and y properties, or
  - an array with x and y values.
- has `add`, `sub`, `mul`, `div`, `cross`, `dot`, and `normalized` methods.
- has `hypot`, `hypotSq` properties.
- has `magnitude`, `magnitudeSq` properties which are aliases for `hypot` and `hypotSq`.
- has `angleWithOrigin` property and `angleTo` method.
- has `equals` and `isCloseTo` methods.

*/

import { fixedFloat, lerp, NumberSchema, type Interpable, type Lerpable } from '@utils';
import { composeArray, composeStruct } from '@strings';
import { z } from 'zod';
import { Angle, radians } from './Angle';
import { Axis } from './Axis';
import { GEOM_TOLERANCE, SERIALIZATION_PRECISION } from './GeomConstants';
import type { IPath, ParamPoint } from './IPath';
import type { PointObject } from './IPoint';
import type { Rect } from './Rect';

// #region Free Functions

/**
 * Returns true if the two point arrays are equal.
 * @param a The first point array.
 * @param b The second point array.
 * @returns True if the two point arrays are equal.
 */
export function pointArraysAreEqual(a: Point[], b: Point[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((aPoint, i) => aPoint.equals(b[i]));
}

// #endregion

// #region Schema

export const PointSchema = z
  .array(NumberSchema, {
    message: 'Point must be an array of two numbers, [x, y]'
  })
  .length(2, { message: 'Point must have exactly two elements' })
  .transform(([x, y]) => new Point(x, y));

// #endregion

export class Point implements PointObject, IPath, Lerpable, Interpable {
  readonly x: number;
  readonly y: number;

  // #region Constants

  static ZERO = new Point(0, 0);

  // #endregion

  // #region Constructors

  constructor(
    a:
      | number
      | [number, number]
      | { x: number; y: number }
      | { axis: Axis; major: number; minor: number }
      | Point
      | Float32Array = 0,
    b?: number
  ) {
    if (typeof a === 'number' && typeof b === 'number') {
      // a and b are numbers
      this.x = a;
      this.y = b;
    } else if (Array.isArray(a)) {
      // a is an array of two numbers
      this.x = a[0];
      this.y = a[1];
    } else if (a instanceof Point) {
      // a is a Point
      this.x = a.x;
      this.y = a.y;
    } else if (a instanceof Float32Array && a.length === 2) {
      // a is a Float32Array of two numbers
      this.x = a[0];
      this.y = a[1];
    } else if (typeof a === 'object') {
      // a is an object
      if ('x' in a && 'y' in a) {
        // a has x and y properties
        this.x = a.x;
        this.y = a.y;
      } else if ('axis' in a && 'major' in a && 'minor' in a) {
        // a has axis, major, and minor properties
        const { axis, major, minor } = a;
        if (axis === Axis.X) {
          this.x = major;
          this.y = minor;
        } else {
          this.x = minor;
          this.y = major;
        }
      } else {
        throw new Error('Invalid Point arguments');
      }
    } else if (typeof a === 'number' && b === undefined) {
      // a is a single number (default value of 0) and b is undefined
      this.x = a;
      this.y = a;
    } else {
      throw new Error('Invalid Point arguments');
    }

    Object.freeze(this);
  }

  // #endregion

  // #region Attributes

  get isValid(): boolean {
    return (this.x as unknown) != null && (this.y as unknown) != null;
  }

  get isZero(): boolean {
    return this.x === 0 && this.y === 0;
  }

  // #endregion

  // #region Methods

  reversed(): this {
    return this;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  flattened(flatness: number): ParamPoint[] {
    return [{ t: 0, p: this }];
  }

  normalized(): Point {
    const mag = this.magnitude;
    if (mag === 0) return this;
    return this.div(mag);
  }

  // #endregion

  // #region IPath Conformance

  get start(): this {
    return this;
  }

  get end(): this {
    return this;
  }

  // eslint-disable-next-line @typescript-eslint/class-literal-property-style
  get length(): number {
    return 0;
  }

  get bounds(): Rect {
    throw new Error('Cannot get bounds of a Point');
  }

  // The point at a given parameter t is always the point itself.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  pointAt(t: number): this {
    return this;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  lengthAt(t: number): number {
    return 0;
  }

  // The derivative of a point as a degenerate path is arbitrarily set to (0, 0).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  derivative(t: number): Point {
    return Point.ZERO;
  }

  // The normal of a point as a degenerate path is arbitrarily set to (0, 0).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  normal(t: number): Point {
    return Point.ZERO;
  }

  // The angle of a point as a degenerate path is arbitrarily set to 0.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  angle(t: number): Angle {
    return Angle.ZERO;
  }

  project(p: Point): { p: Point; t: number; d: number } {
    return { p: this, t: 0, d: this.distanceTo(p) };
  }

  intersectsRect(r: Rect): boolean {
    return r.containsPoint(this);
  }

  lerp(t: number, end: this): this {
    return new Point(lerp(t, [this.x, end.x]), lerp(t, [this.y, end.y])) as this;
  }

  interp(end: this): (t: number) => this {
    return (t: number) => this.lerp(t, end);
  }

  isIsomorphicTo(other: IPath): boolean {
    return other instanceof Point;
  }

  isSamePath(other: IPath): boolean {
    return other instanceof Point && this.equals(other);
  }

  recalculateCache(): void {
    // No cache
  }

  // #endregion

  // #region Comparison

  equals(other: Point | PointObject): boolean {
    if (this === other) return true;
    return this.x === other.x && this.y === other.y;
  }

  isCloseTo(other: Point | PointObject, tolerance = 1e-10): boolean {
    return (
      Math.abs(this.x - other.x) <= tolerance && Math.abs(this.y - other.y) <= tolerance
    );
  }

  // #endregion

  // #region Type Conversion

  get object(): PointObject {
    return { x: this.x, y: this.y };
  }

  get array(): number[] {
    return [this.x, this.y];
  }

  get typedArray(): Float32Array {
    return new Float32Array([this.x, this.y]);
  }

  get ckPoint(): Float32Array {
    return this.typedArray;
  }

  // #endregion

  // #region Immutable Setters

  withX(newX: number): Point {
    return new Point(newX, this.y);
  }

  withY(newY: number): Point {
    return new Point(this.x, newY);
  }

  // #endregion

  // #region Geometric Operations

  /**
   * Calculates the hypotenuse of the point from the origin (0, 0) using the Pythagorean theorem.
   *
   * This is equivalent to the distance of the point from the origin,
   * or the magnitude of the point when treated as a vector.
   *
   * @returns {number} The length of the hypotenuse.
   */
  get hypot(): number {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  }

  /**
   * Gets the magnitude of the point construed as a vector.
   * The magnitude is the length of the vector from the origin to this point.
   *
   * @returns {number} The magnitude of the point.
   */
  get magnitude(): number {
    return this.hypot;
  }

  /**
   * Calculates the square of the hypotenuse of the point from the origin.
   *
   * This is equivalent to the sum of the squares of the x and y coordinates.
   * Useful for performance optimization when comparing distances.
   *
   * @returns The square of the hypotenuse.
   */
  get hypotSq(): number {
    return this.x * this.x + this.y * this.y;
  }

  /**
   * Gets the square of the magnitude of the point construed as a vector.
   * This is equivalent to the square of the hypotenuse.
   *
   * @returns The square of the magnitude.
   */
  get magnitudeSq(): number {
    return this.hypotSq;
  }

  /**
   * Calculates the distance from this point to another point.
   *
   * @param other - The other point to which the distance is calculated. It can be an instance of `Point` or an object that has `x` and `y` properties.
   * @returns The Euclidean distance between this point and the other point.
   */
  distanceTo(other: Point | PointObject): number {
    const dx = this.x - other.x;
    const dy = this.y - other.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Calculates the squared distance from this point to another point.
   *
   * This method is useful for performance optimization when comparing distances,
   * as it avoids the computational cost of a square root operation.
   *
   * @param other - The other point to which the squared distance is calculated.
   *                 It can be an instance of `Point` or an object with `x` and `y` properties.
   * @returns The squared distance between this point and the other point.
   */
  distanceToSq(other: Point | PointObject): number {
    const dx = this.x - other.x;
    const dy = this.y - other.y;
    return dx * dx + dy * dy;
  }

  /**
   * Calculates the angle between the point and the origin (0, 0).
   *
   * @returns {Angle} An `Angle` object representing the angle in radians.
   */
  get angleWithOrigin(): Angle {
    return radians(Math.atan2(this.y, this.x));
  }

  /**
   * Creates a new Point from an angle.
   * The point will be on the unit circle at the given angle.
   *
   * @param angle - The angle specifying the position on the unit circle
   * @returns A point on the unit circle at the given angle
   */
  static fromAngle(angle: Angle): Point {
    return new Point(Math.cos(angle.radians), Math.sin(angle.radians));
  }

  /**
   * Calculates the angle from this point to another point.
   *
   * @param other - The other point to which the angle is calculated.
   * @returns An `Angle` object representing the angle in radians from this point to the other point.
   */
  angleTo(other: Point): Angle {
    const angleRadians = Math.atan2(other.y - this.y, other.x - this.x);
    return radians(angleRadians);
  }

  /**
   * Calculates the midpoint between the current point and another point.
   *
   * @param other - The other point to which the midpoint is calculated. It can be an instance of `Point` or an object that conforms to the `PointObject` interface.
   * @returns A new `Point` instance representing the midpoint between the current point and the specified `other` point.
   */
  halfwayTo(other: Point | PointObject): Point {
    return this.add(other).div(2);
  }

  /**
   * Adds the coordinates of another point or point-like object to this point.
   *
   * @param other - The point or point-like object to add. It can be an instance of `Point` or an object with `x` and `y` properties.
   * @returns A new `Point` instance representing the sum of this point and the given point or point-like object.
   */
  add(other: Point | PointObject): Point {
    return new Point(this.x + other.x, this.y + other.y);
  }

  /**
   * Subtracts the coordinates of another point from this point.
   *
   * @param other - The point or point-like object to subtract from this point.
   * @returns A new `Point` instance representing the result of the subtraction.
   */
  sub(other: Point | PointObject): Point {
    return new Point(this.x - other.x, this.y - other.y);
  }

  /**
   * Multiplies the current point's coordinates by a given scalar.
   *
   * @param scalar - The number to multiply the point's coordinates by.
   * @returns A new `Point` instance with the coordinates scaled by the given scalar.
   */
  mul(scalar: number): Point {
    return new Point(this.x * scalar, this.y * scalar);
  }

  /**
   * Negates the coordinates of the current point.
   *
   * @returns A new `Point` instance with both `x` and `y` coordinates negated.
   */
  neg(): Point {
    return new Point(-this.x, -this.y);
  }

  /**
   * Divides the current point's coordinates by the given scalar value.
   *
   * @param scalar - The value by which to divide the point's coordinates.
   * @returns A new `Point` instance with the coordinates divided by the scalar.
   */
  div(scalar: number): Point {
    return new Point(this.x / scalar, this.y / scalar);
  }

  /**
   * Calculates the dot product of this point with another point.
   *
   * @param other - The other point or point-like object to calculate the dot product with.
   * @returns The dot product of the two points.
   */
  dot(other: Point | PointObject): number {
    return this.x * other.x + this.y * other.y;
  }

  /**
   * Calculates the cross product of this point with another point.
   *
   * @param other - The other point or point-like object to calculate the cross product with.
   * @returns The cross product of the two points.
   */
  cross(other: Point | PointObject): number {
    return this.x * other.y - this.y * other.x;
  }

  /**
   * Returns a new Point that is perpendicular to this point (rotated 90 degrees counterclockwise).
   */
  perpendicular(): Point {
    return new Point(-this.y, this.x);
  }

  /**
   * Rotates this point around a given center point by a specified angle.
   *
   * @param center - The point to rotate around.
   * @param angle - The angle to rotate by, in radians.
   * @returns A new `Point` that is the result of the rotation.
   */
  rotateAround(center: Point, angle: Angle): Point {
    const cos = Math.cos(angle.radians);
    const sin = Math.sin(angle.radians);
    const x = this.x - center.x;
    const y = this.y - center.y;
    return new Point(center.x + x * cos - y * sin, center.y + x * sin + y * cos);
  }

  /**
   * Computes the weighted sum of two points.
   *
   * @param x - The weight for the first point.
   * @param a - The first point.
   * @param y - The weight for the second point.
   * @param b - The second point.
   * @returns The resulting point after computing the weighted sum.
   */
  static weightedSum(x: number, a: Point, y: number, b: Point) {
    return a.mul(x).add(b.mul(y));
  }

  // The angle you need to turn "side0" counterclockwise to make it collinear with "side1".
  static angle(side0: Point, side1: Point): number {
    const ax = side0.x;
    const ay = side0.y;
    const bx = side1.x;
    const by = side1.y;

    const cross = ax * by - ay * bx;
    const dot = ax * bx + ay * by;

    if (Math.abs(dot) < GEOM_TOLERANCE) {
      if (Math.abs(cross) < GEOM_TOLERANCE) return 0;

      if (cross < -GEOM_TOLERANCE) return (3 * Math.PI) / 2;
      return Math.PI / 2;
    }

    if (Math.abs(cross) < GEOM_TOLERANCE) {
      if (dot < -GEOM_TOLERANCE) return Math.PI;
      return 0.0;
    }

    const atan2 = Math.atan2(cross, dot);
    if (cross >= -GEOM_TOLERANCE) return atan2;
    return Math.PI * 2.0 + atan2;
  }

  rounded(fractionDigits = 0): Point {
    return new Point(
      fixedFloat(this.x, fractionDigits),
      fixedFloat(this.y, fractionDigits)
    );
  }

  majorAxis(axis: Axis): number {
    switch (axis) {
      case Axis.X:
        return this.x;
      case Axis.Y:
        return this.y;
    }
  }

  minorAxis(axis: Axis): number {
    switch (axis) {
      case Axis.X:
        return this.y;
      case Axis.Y:
        return this.x;
    }
  }

  // #endregion

  // #region Serialization

  toJSON(): unknown {
    return [
      fixedFloat(this.x, SERIALIZATION_PRECISION),
      fixedFloat(this.y, SERIALIZATION_PRECISION)
    ];
  }

  static fromJSON(json: unknown): Point {
    return PointSchema.parse(json);
  }

  // #endregion

  // #region String Conversion

  /**
   * Converts the point coordinates to a string representation in object format.
   *
   * @param fractionDigits - Optional. The number of digits to appear after the decimal point.
   * @returns A string representing the point coordinates in the format `{x: x, y: y}`.
   */
  toString(fractionDigits?: number): string {
    return composeStruct(
      [
        ['x', this.x],
        ['y', this.y]
      ],
      fractionDigits
    );
  }

  /**
   * Converts the point coordinates to a string representation in array format.
   *
   * @param fractionDigits - Optional. The number of digits to appear after the decimal point.
   * @returns A string representing the point coordinates in the format `[x, y]`.
   */
  toArrayString(fractionDigits?: number): string {
    return composeArray([this.x, this.y], fractionDigits);
  }
}
